import { DataSettingGlobalStyle } from '@/app/editor/iframe/atom/service/data/DataSettingGlobalStyle';
import { renderTabSettings } from '@/app/editor/sidebar/global-style/renderTabSetting';
import { base } from '@/app/editor/sidebar/constant';
import { app } from '@/store';
import HELPER from '@/app/utils/HELPER';
import store from '@/app/store/store';
import { DESIGN_GROUP, ELEMENTS_MAP } from '@/constants/elements-map';
import { MODULES_MAP } from '@/constants/modules-map';
import { ROWS_MAP } from '@/constants/rows-map';
import { SETTING_GLOBAL_STYLE } from '@/constants/setting-global-style';
import { cloneDeep } from '@/utils/common';
import { convertButtonOptionToCssObject, generateClassName } from '@modules/global-style';
import replaceEscapeHtml from '@/app/utils/replaceEscapeHtml';
import entitiesHTML from '@/app/utils/entitiesHTML';
import { CLASS_GS_CUSTOM, DISPLAY_TITLE_BUTTON } from '@modules/editor-sidebar';
import { convertLiquidTagToTagHtml, revertTagHtmlToLiquidTag } from '@/app/utils/handleLiquidTagOnGS';
import replaceBrokenHTMLTemplateContent from '@/app/utils/replaceBrokenHTMLTemplateContent';
import i18n from '@/i18n';
import { convertButtonOptionToCssObject as convertButtonOptionToCssObject2 } from '@/app/editor/sidebar/global-style/button-styles';

/**
 * export
 */
export class Common {
  public snippetType: string;

  /**
   * getPreset
   */
  getPreset(isRenderClass = true) {
    if (!app) return;

    const currentPreset = (app as any).$store.state.sidebar?.currentPresetInPage;
    if (!currentPreset) {
      return;
    }
    return isRenderClass ? this.mapClassForPresetOptions(currentPreset) : currentPreset;
  }

  /**
   * getPresetByPresetId
   */
  getPresetByPresetId(presetId: any, isRenderClass = true) {
    if (!app) return;

    const presets = (app as any).$store.state.sidebar.presets;
    if (!presets) return;
    const preset = presets.find((item: any) => item.id == +presetId);
    if (!preset) {
      return;
    }
    return isRenderClass ? this.mapClassForPresetOptions(preset) : preset;
  }

  /**
   *
   */
  mapClassForPresetOptions(preset: any) {
    preset = cloneDeep(preset);
    const presetOptions = cloneDeep(preset.presetOptions);
    Object.keys(presetOptions).forEach((key) => {
      presetOptions[key].forEach((item: any) => {
        item.class = generateClassName(item);
      });
    });
    preset.presetOptions = presetOptions;
    return preset;
  }

  /**
   * getDataHeading
   */
  getDataHeading(presetId?: any, isRenderClass?: boolean) {
    let defaultPreset: any;
    if (presetId) {
      defaultPreset = this.getPresetByPresetId(presetId, isRenderClass);
    } else {
      defaultPreset = this.getPreset();
    }
    if (!defaultPreset) {
      return [];
    }

    return (
      defaultPreset.presetOptions && defaultPreset.presetOptions.texts.filter((text: any) => text.subType === 'heading')
    );
  }

  /**
   * getDataParagraph
   */
  getDataParagraph(presetId?: any, isRenderClass?: boolean) {
    let defaultPreset: any;
    if (presetId) {
      defaultPreset = this.getPresetByPresetId(presetId, isRenderClass);
    } else {
      defaultPreset = this.getPreset();
    }
    if (!defaultPreset) {
      return [];
    }
    return (
      defaultPreset.presetOptions &&
      defaultPreset.presetOptions.texts.filter((text: any) => text.subType === 'paragraph')
    );
  }

  /**
   * getDataBorder
   */
  getDataBorder(borderKey?: any, presetId?: any, isRenderClass?: boolean) {
    let defaultPreset: any;
    if (presetId) {
      defaultPreset = this.getPresetByPresetId(presetId, isRenderClass);
    } else {
      defaultPreset = this.getPreset();
    }
    if (!defaultPreset) {
      return [];
    }
    if (borderKey) {
      if (defaultPreset.presetOptions && defaultPreset.presetOptions.borders) {
        const borders = defaultPreset.presetOptions.borders;
        return borders.filter((border: any) => border.key == borderKey || !border.key);
      }
    } else {
      return defaultPreset.presetOptions && defaultPreset.presetOptions.borders;
    }
  }

  /**
   * getDataHeadingParagraph
   */
  getDataHeadingParagraph() {
    return this.getDataHeading().concat(this.getDataParagraph());
  }

  /**
   * getDataCorner
   */
  getDataCorner(cornerKey?: any, presetId?: any, isRenderClass?: boolean) {
    let defaultPreset: any;
    if (presetId) {
      defaultPreset = this.getPresetByPresetId(presetId, isRenderClass);
    } else {
      defaultPreset = this.getPreset();
    }
    if (!defaultPreset) {
      return [];
    }
    if (cornerKey) {
      if (defaultPreset.presetOptions && defaultPreset.presetOptions.corners) {
        const corners = defaultPreset.presetOptions.corners;
        return corners.filter((corner: any) => corner.key == cornerKey || !corner.key);
      }
    } else {
      return defaultPreset.presetOptions && defaultPreset.presetOptions.corners;
    }
  }

  /**
   * getDataButtonType
   */
  getDataButtonType(buttonKey?: any, presetId?: any, isRenderClass?: boolean) {
    let defaultPreset: any;
    if (presetId) {
      defaultPreset = this.getPresetByPresetId(presetId, isRenderClass);
    } else {
      defaultPreset = this.getPreset();
    }
    if (!defaultPreset) {
      return [];
    }
    if (buttonKey) {
      if (defaultPreset.presetOptions && defaultPreset.presetOptions.buttons) {
        const buttons = defaultPreset.presetOptions.buttons;
        return buttons.filter((button: any) => button.key == buttonKey || !button.key);
      }
    } else {
      return defaultPreset.presetOptions && defaultPreset.presetOptions.buttons;
    }
  }

  /**
   * getDataButtonStyle
   */
  getDataButtonStyle() {
    return [
      {
        id: 1,
        name: 'Large',
        class: 'gf_gs-button---large',
      },
      {
        id: 2,
        name: 'Medium',
        class: 'gf_gs-button---medium',
      },
      {
        id: 3,
        name: 'Small',
        class: 'gf_gs-button---small',
      },
    ];
  }

  /**
   * headingClass
   */
  getDataSpacing(spacingKey?: any, presetId?: any, isRenderClass?: boolean) {
    let defaultPreset: any;
    if (presetId) {
      defaultPreset = this.getPresetByPresetId(presetId, isRenderClass);
    } else {
      defaultPreset = this.getPreset();
    }
    if (!defaultPreset) {
      return [];
    }
    if (spacingKey) {
      if (defaultPreset.presetOptions && defaultPreset.presetOptions.spacings) {
        const spacings = defaultPreset.presetOptions.spacings;
        return spacings.filter((spacing: any) => spacing.key == spacingKey || !spacing.key);
      }
    } else {
      return defaultPreset.presetOptions && defaultPreset.presetOptions.spacings;
    }
  }

  /**
   * getFieldsByGsId
   */
  getFieldsByGsId(gsId = '') {
    const fields = DESIGN_GROUP[gsId];
    return (fields && Object.keys(fields)) || [];
  }

  /**
   *
   */
  buildClassSpacing(spacing: any = '', inherit = '') {
    const signature = this.getSignature(spacing);
    return `gf_gs-spacing-${signature}-${this.convertSpacingKey(inherit)}`;
  }

  /**
   *
   */
  convertSpacingKey(inherit = '') {
    let newData = '';
    if (!inherit) {
      return '';
    }
    switch (inherit) {
      case 'margin-top':
        newData = 'm-top';
        break;
      case 'margin-right':
        newData = 'm-right';
        break;
      case 'margin-bottom':
        newData = 'm-bottom';
        break;
      case 'margin-left':
        newData = 'm-left';
        break;
      case 'padding-top':
        newData = 'p-top';
        break;
      case 'padding-right':
        newData = 'p-right';
        break;
      case 'padding-bottom':
        newData = 'p-bottom';
        break;
      case 'padding-left':
        newData = 'p-left';
        break;
    }
    return newData;
  }
  /**
   *
   */
  headingClass() {
    return this.getDataHeading().map((item: any) => item.class);
  }

  /**
   * paragraphClass
   */
  paragraphClass() {
    return this.getDataParagraph().map((item: any) => item.class);
  }

  /**
   * headingParagraphClass
   */
  headingParagraphClass() {
    return this.headingClass().concat(this.paragraphClass());
  }

  /**
   * buttonStyleClass
   */
  buttonSizeClass() {
    return ['gf_gs-button---large', 'gf_gs-button---medium', 'gf_gs-button---small'];
  }

  /**
   * buttonTypeClass
   */
  buttonTypeClass() {
    return this.getDataButtonType().map((item: any) => item.class);
  }

  /**
   * borderClass
   */
  borderClass() {
    return this.getDataBorder().map((item: any) => item.class);
  }

  /**
   * cornerClass
   */
  cornerClass() {
    return this.getDataCorner().map((item: any) => item.class);
  }

  /**
   * templateActionEdit
   */
  templateActionEdit(type: string, styleId: number, dataStyle = '') {
    return `<div class='append' data-type='${type}'><i class='icon icon-t-blog' data-id='${styleId}' data-style='${dataStyle}' data-type='${type}'></i></div>`;
  }

  /**
   * headingParagraphTemplate
   */
  headingParagraphTemplate(gsId = '') {
    const options = this.headingParagraphOptions();
    let html = [];
    html.push(`<div id="gf_gs-group-setting-${gsId}">`);
    html.push(
      "<div class='gfSGSubTitle'>Text Styles<span class='icon icon-warn-circle gf_gs-icon-tooltip'></span></div>",
    );
    html.push(
      `<select custom-class="gf_gs-select gf_gs-select-text" value="${CLASS_GS_CUSTOM}" data-disable="false" name="${gsId}" id="${gsId}" class="gf_gs-select-render" style="display: none;">`,
    );
    html = html.concat(options);
    html.push('</select>');
    html.push('</div>');
    return html.join('');
  }

  /**
   * headingParagraphOptions
   */
  headingParagraphOptions() {
    const heading = this.getDataHeading();
    const paragraph = this.getDataParagraph();
    const group = heading.concat(paragraph);
    const hasSetupLink = !group || !group.length;
    const prepend = `<div class='prepend'>
    <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M2.87671 1C2.66866 1 2.5 1.17607 2.5 1.39326V3.2809C2.5 3.49809 2.66866 3.67416 2.87671 3.67416H3.47945C3.6875 3.67416 3.85616 3.49809 3.85616 3.2809V2.41573H7.32192V13.5843H6.49315C6.28512 13.5843 6.11644 13.7604 6.11644 13.9775V14.6067C6.11644 14.8239 6.28512 15 6.49315 15H9.50685C9.71488 15 9.88356 14.8239 9.88356 14.6067V13.9775C9.88356 13.7604 9.71488 13.5843 9.50685 13.5843H8.67808V2.41573H12.1438V3.2809C12.1438 3.49809 12.3125 3.67416 12.5205 3.67416H13.1233C13.3313 3.67416 13.5 3.49809 13.5 3.2809V1.39326C13.5 1.17607 13.3313 1 13.1233 1H2.87671Z' fill='currentColor'/>
    </svg>
    </div>`;
    const html = [];
    html.push(
      `<option prepend="${prepend}" custom-class="${
        hasSetupLink ? '' : 'gf_gs-custom'
      }" value="${CLASS_GS_CUSTOM}">Custom</option>`,
    );
    if (heading && heading.length) {
      html.push('<option custom-class="opt-group">HEADING</option>');
      heading.forEach((item: any) => {
        const actionEdit = this.templateActionEdit('text', item.id);
        html.push(
          `<option append="${actionEdit}" prepend="${prepend}" custom-class="" value="${item.class}">${item.name}</option>`,
        );
      });
    }
    if (paragraph && paragraph.length) {
      html.push('<option custom-class="opt-group">PARAGRAPH</option>');
      paragraph.forEach((item: any) => {
        const actionEdit = this.templateActionEdit('text', item.id);
        html.push(
          `<option append="${actionEdit}" prepend="${prepend}" custom-class="" value="${item.class}">${item.name}</option>`,
        );
      });
    }
    return html;
  }

  /**
   * headingTemplate
   */
  headingTemplate(gsId = '') {
    let html = [];
    const options = this.headingOptions();
    html.push(`<div id="gf_gs-group-setting-${gsId}">`);
    html.push(
      '<div class="gfSGSubTitle">Text Styles<span class="icon icon-warn-circle gf_gs-icon-tooltip"></span></div>',
    );
    html.push(
      `<select custom-class="gf_gs-select gf_gs-select-text" value="${CLASS_GS_CUSTOM}" data-disable="false" name="${gsId}" id="${gsId}" class="gf_gs-select-render" style="display: none;">`,
    );
    html = html.concat(options);
    html.push('</select>');
    html.push('</div>');
    return html.join('');
  }

  /**
   * headingOptions
   */
  headingOptions() {
    const heading = this.getDataHeading();
    const hasSetupLink = !heading || !heading.length;
    const prepend = `<div class='prepend'>
    <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M2.87671 1C2.66866 1 2.5 1.17607 2.5 1.39326V3.2809C2.5 3.49809 2.66866 3.67416 2.87671 3.67416H3.47945C3.6875 3.67416 3.85616 3.49809 3.85616 3.2809V2.41573H7.32192V13.5843H6.49315C6.28512 13.5843 6.11644 13.7604 6.11644 13.9775V14.6067C6.11644 14.8239 6.28512 15 6.49315 15H9.50685C9.71488 15 9.88356 14.8239 9.88356 14.6067V13.9775C9.88356 13.7604 9.71488 13.5843 9.50685 13.5843H8.67808V2.41573H12.1438V3.2809C12.1438 3.49809 12.3125 3.67416 12.5205 3.67416H13.1233C13.3313 3.67416 13.5 3.49809 13.5 3.2809V1.39326C13.5 1.17607 13.3313 1 13.1233 1H2.87671Z' fill='currentColor'/>
    </svg>
    </div>`;
    const html = [];
    html.push(
      `<option prepend="${prepend}" custom-class="${
        hasSetupLink ? '' : 'gf_gs-custom'
      }" value="${CLASS_GS_CUSTOM}">Custom</option>`,
    );
    heading.forEach((item: any) => {
      const actionEdit = this.templateActionEdit('text', item.id);
      html.push(
        `<option append="${actionEdit}" prepend="${prepend}" custom-class="" value="${item.class}">${item.name}</option>`,
      );
    });
    return html;
  }

  /**
   * paragraphTemplate
   */
  paragraphTemplate(gsId = '') {
    let html = [];
    const options = this.paragraphOptions();
    html.push(`<div id="gf_gs-group-setting-${gsId}">`);
    html.push(
      '<div class="gfSGSubTitle">Text Styles<span class="icon icon-warn-circle gf_gs-icon-tooltip"></span></div>',
    );
    html.push(
      `<select custom-class="gf_gs-select gf_gs-select-text" value="${CLASS_GS_CUSTOM}" data-disable="false" name="${gsId}" id="${gsId}" class="gf_gs-select-render" style="display: none;">`,
    );
    html = html.concat(options);
    html.push('</select>');
    html.push('</div>');
    return html.join('');
  }

  /**
   * paragraphOptions
   */
  paragraphOptions() {
    const paragraph = this.getDataParagraph();
    const hasSetupLink = !paragraph || !paragraph.length;
    const prepend = `<div class='prepend'>
    <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M2.87671 1C2.66866 1 2.5 1.17607 2.5 1.39326V3.2809C2.5 3.49809 2.66866 3.67416 2.87671 3.67416H3.47945C3.6875 3.67416 3.85616 3.49809 3.85616 3.2809V2.41573H7.32192V13.5843H6.49315C6.28512 13.5843 6.11644 13.7604 6.11644 13.9775V14.6067C6.11644 14.8239 6.28512 15 6.49315 15H9.50685C9.71488 15 9.88356 14.8239 9.88356 14.6067V13.9775C9.88356 13.7604 9.71488 13.5843 9.50685 13.5843H8.67808V2.41573H12.1438V3.2809C12.1438 3.49809 12.3125 3.67416 12.5205 3.67416H13.1233C13.3313 3.67416 13.5 3.49809 13.5 3.2809V1.39326C13.5 1.17607 13.3313 1 13.1233 1H2.87671Z' fill='currentColor'/>
    </svg>
    </div>`;
    const html = [];
    html.push(
      `<option prepend="${prepend}" custom-class="${
        hasSetupLink ? '' : 'gf_gs-custom'
      }" value="${CLASS_GS_CUSTOM}">Custom</option>`,
    );
    paragraph.forEach((item: any) => {
      const actionEdit = this.templateActionEdit('text', item.id);
      html.push(
        `<option append="${actionEdit}" prepend="${prepend}" custom-class="" value="${item.class}">${item.name}</option>`,
      );
    });
    return html;
  }

  /**
   * buttonTemplate
   */
  buttonTemplate(gsTypeId = '', gsStyleId = '', buttonKey: any) {
    let html = '';
    html += this.buttonTypeTemplate(gsTypeId, buttonKey);
    html += this.buttonStyleTemplate(gsStyleId);
    return html;
  }

  /**
   * buttonTypeTemplate
   */
  buttonTypeTemplate(gsId = '', buttonKey: any) {
    let html = [];
    const options = this.buttonTypeOptions(buttonKey);
    html.push(`<div id="gf_gs-group-setting-${gsId}">`);
    html.push(
      '<div class="gfSGSubTitle">Button Styles<span class="icon icon-warn-circle gf_gs-icon-tooltip"></span></div>',
    );
    html.push(
      `<select custom-class="gf_gs-select gf_gs-select-button" value="${CLASS_GS_CUSTOM}" data-disable="false" name="${gsId}" id="${gsId}" class="gf_gs-select-render" style="display: none;">`,
    );
    html = html.concat(options);
    html.push('</select>');
    html.push('</div>');
    return html.join('');
  }

  /**
   * buttonTypeOptions
   */
  buttonTypeOptions(buttonKey: any) {
    const type = this.getDataButtonType(buttonKey);
    const hasSetupLink = !type || !type.length;
    const html = [];
    html.push(
      `<option custom-class="${
        hasSetupLink ? 'gf_gs-has-setup-link' : 'gf_gs-custom'
      }"  value="${CLASS_GS_CUSTOM}">Custom</option>`,
    );
    type.forEach((item: any) => {
      const stylePreviewDropButton = convertButtonOptionToCssObject2(item);
      const style = stylePreviewDropButton ? stylePreviewDropButton.button : '';

      const actionEdit = this.templateActionEdit('button', item.id);
      const disName = DISPLAY_TITLE_BUTTON[item.key] || 'Custom Button';
      html.push(`<option
      custom-class=""
      data-value='${item.class}'
      append="${actionEdit}"
      prepend="<div class='prepend gf_gs-button'>
      <button style='${replaceEscapeHtml(HELPER.objectToStyle(style))}'
      class='${item.class}'>${disName}</button></div>"
      value="${item.class}">
      ${item.name}
      </option>`);
    });
    return html;
  }

  /**
   * buttonStyleTemplate
   */
  buttonStyleTemplate(gsId = '') {
    const style = this.getDataButtonStyle();
    let html = [];
    const options = this.buttonStyleOptions();
    const defaultValue = (style && style.length && style[0].class) || '';
    html.push(`<div id="gf_gs-group-setting-${gsId}">`);
    html.push(`<div class="gfSGSubTitle" id="gfSGSubTitle_${gsId}">Button Size</div>`);
    html.push(
      `<select custom-class="gf_gs-select" data-disable="false" name="${gsId}" id="${gsId}" value="${defaultValue}" class="gf_gs-select-render--size" style="display: none;">`,
    );
    html = html.concat(options);
    html.push('</select>');
    html.push('</div>');
    return html.join('');
  }

  /**
   * buttonStyleOptions
   */
  buttonStyleOptions() {
    const style = this.getDataButtonStyle();
    const html: any = [];
    style.forEach((item) => {
      const actionEdit = this.templateActionEdit('button', item.id, 'button-style');
      html.push(`<option append="${actionEdit}" value="${item.class}">${item.name}</option>`);
    });
    return html;
  }

  /**
   * borderTemplate
   */
  borderTemplate(gsId = ''): string {
    const options = this.borderOptions();
    let html = [];
    html.push(`<div id="gf_gs-group-setting-${gsId}">`);
    html.push(
      '<div class="gfSGSubTitle">Border Style<span class="icon icon-warn-circle gf_gs-icon-tooltip"></span></div>',
    );
    html.push(
      `<select custom-class="gf_gs-select gf_gs-select-text" value="" data-disable="false" name="${gsId}" id="${gsId}" class="gf_gs-select-render" style="display: none;">`,
    );
    html = html.concat(options);
    html.push('</select>');
    html.push('</div>');
    return html.join('');
  }

  /**
   * borderOptions
   */
  borderOptions() {
    const border = this.getDataBorder();
    const hasSetupLink = !border || !border.length;
    const prepend = `<div class='prepend'>
      <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path fill-rule='evenodd' clip-rule='evenodd' d='M1 4C1 3.72386 1.22386 3.5 1.5 3.5H15V12.5H1.5C1.22386 12.5 1 12.2761 1 12C1 11.7239 1.22386 11.5 1.5 11.5H14V4.5H1.5C1.22386 4.5 1 4.27614 1 4Z' fill='currentColor'/>
      </svg>
    </div>`;
    const html = [];
    html.push(
      `<option prepend="${prepend}" custom-class="${hasSetupLink ? '' : 'gf_gs-custom'}" value="">Custom</option>`,
    );
    border.forEach((item: any) => {
      const actionEdit = this.templateActionEdit('border', item.id);
      html.push(
        `<option append="${actionEdit}" prepend="${prepend}" custom-class="" value="${item.class}">${item.name}</option>`,
      );
    });
    return html;
  }

  /**
   * cornerTemplate
   */
  cornerTemplate(gsId = ''): string {
    const options = this.cornerOptions();
    let html = [];
    html.push(`<div id="gf_gs-group-setting-${gsId}">`);
    html.push(
      '<div class="gfSGSubTitle">Corner Style<span class="icon icon-warn-circle gf_gs-icon-tooltip"></span></div>',
    );
    html.push(
      `<select custom-class="gf_gs-select gf_gs-select-text" value="" data-disable="false" name="${gsId}" id="${gsId}" class="gf_gs-select-render" style="display: none;">`,
    );
    html = html.concat(options);
    html.push('</select>');
    html.push('</div>');
    return html.join('');
  }

  /**
   * cornerOptions
   */
  cornerOptions() {
    const corner = this.getDataCorner();
    const hasSetupLink = !corner || !corner.length;
    const prepend = `<div class='prepend'>
    <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path fill-rule='evenodd' clip-rule='evenodd' d='M4.75 3.75H2.75V2.75H4.75C9.44442 2.75 13.25 6.55558 13.25 11.25V13.25H12.25V11.25C12.25 7.10786 8.89214 3.75 4.75 3.75Z' fill='currentColor'/>
    </svg>
    </div>`;
    const html = [];
    html.push(
      `<option prepend="${prepend}" custom-class="${hasSetupLink ? '' : 'gf_gs-custom'}" value="">Custom</option>`,
    );
    corner.forEach((item: any) => {
      const actionEdit = this.templateActionEdit('corner', item.id);
      html.push(
        `<option append="${actionEdit}" prepend="${prepend}" custom-class="" value="${item.class}">${item.name}</option>`,
      );
    });
    return html;
  }

  /**
   * convertRemEmToPx
   */
  spacingTemplate(): string {
    const spacing = this.getDataSpacing();
    if (!spacing) return '';

    const html = [];
    html.push('<ul class="gf_gs-dropdown-spacing">');
    spacing.forEach((item: any) => {
      const label = `${item.name}`;
      html.push(`<li class="" data-value="${this.getSignature(item)}">${label}</li>`);
    });
    html.push('<li class="gf_gs-custom" data-value="" data-type="spacing">Edit Value</li>');

    html.push('</ul>');
    return html.join('');
  }

  /**
   *
   */
  convertRemEmToPx(value: any = 1, baseSize = 16) {
    const newValue: any = value * baseSize;
    return parseFloat(newValue).toFixed(2);
  }

  /**
   * convertPxToRemEm
   */
  convertPxToRemEm(value: any = 1, baseSize = 16) {
    const newValue: any = value / baseSize;
    return parseFloat(newValue).toFixed(2);
  }

  /**
   * detachUnitAndValue
   */
  detachUnitAndValue(value = '') {
    if (!value) {
      return '';
    }
    // const detech = String(value).match(/^(\d+(?:\.\d+)?)(.*)$/);
    const detech = String(value).match(/(-?[\d.]+)([a-z%]*)/);
    if (!detech || !detech.length || !detech[1] || !detech[2]) {
      return;
    }
    return {
      value: detech[1],
      unit: detech[2],
    };
  }

  /**
   * obj: Object
   * path: string Ex: heading.fontSize
   */
  getValueDeepObjectByPath(obj: any = {}, path: any = '') {
    path = path.split('.');
    for (let i = 0, len = path.length; i < len; i++) {
      obj = obj[path[i]] ? obj[path[i]] : '';
    }
    return obj;
  }

  /**
   * convertGsValueCorner
   */
  convertGsValueCorner(value: any) {
    if (!value) return {};
    const newValue: any = {};
    const cornerValue = value.split(' ');
    const cornerPosition = ['top', 'right', 'bottom', 'left'];
    for (let i = 0; i < cornerPosition.length; i++) {
      let val = 0;
      if (cornerValue.length === 1) {
        val = cornerValue[0];
      } else {
        val = cornerValue[i];
      }
      newValue[cornerPosition[i]] = val;
    }
    return newValue;
  }

  /**
   * item: Object
   */
  convertGsValueItem(item: any = {}, currentSize = '') {
    if (!item) {
      return;
    }
    item = this.convertDataGs(item);
    if (item.type === 'corner') {
      const cornerValue = item.value && item.value.value;
      item.value = {
        ...item.value,
        ...this.convertGsValueCorner(cornerValue),
      };
    }
    if (item.type === 'button') {
      const presetTexts = this.getDataHeadingParagraph();
      const presetCorners = this.getDataCorner();
      const presetBorders = this.getDataBorder();
      const textStyle = item.value.textStyle;
      let dataText = presetTexts.find((tItem: { key: string }) => tItem.key == textStyle[currentSize]);
      dataText = this.convertDataGs(dataText);
      item.value.textStyleValue = dataText ? dataText.value : false;
      if (item?.value?.backgroundColor?.hover || item?.value?.textColor?.hover) {
        item.value.buttonHover = 1;
      }
      if (item?.value?.cornerStyle) {
        const dataCorner = presetCorners.find((cItem: { id: number }) => cItem.id == item.value.cornerStyle);
        item.value.cornerStyleValue = dataCorner ? dataCorner.value : false;
        item.value.cornerStyleValue = {
          ...item.value.cornerStyleValue,
          ...this.convertGsValueCorner(item.value.cornerStyleValue.value),
        };
      } else {
        item.value.cornerStyleValue = {
          bottom: '0px',
          left: '0px',
          right: '0px',
          top: '0px',
        };
      }
      if (item?.value?.borderStyle) {
        const dataBorder = presetBorders.find((bItem: any) => bItem.id == item?.value?.borderStyle);
        item.value.borderStyleValue = dataBorder ? dataBorder.value : false;
      }
      item.value.dropShadowValue = {
        angle: '0',
        blur: '0',
        color: 'transparent',
        distance: '0',
        size: '0',
      };
      if (item?.value?.dropShadow.enabled) {
        item.value.dropShadowValue = item?.value?.dropShadow;
        const valueAndUnit: any =
          (item.value.dropShadowValue.angle && this.detachUnitAndValue(item.value.dropShadowValue.angle)) || {};
        item.value.dropShadowValue.angle = (valueAndUnit && valueAndUnit.value) || '0';
      }
    }
    if (!item[item.type]) {
      item[item.type] = item.value;
    }
    return item;
  }

  /**
   * convertDataFontWeight
   */
  convertDataFontWeight(fontWeight: any) {
    if (fontWeight == 700) {
      return 'bold';
    }
    return fontWeight;
  }
  /**
   * convertDataGs
   */
  convertDataGs(data: any) {
    if (data?.subType === 'heading') {
      data.value.fontFamily = data.value.headingFont;
    } else if (data?.subType === 'paragraph') {
      data.value.fontFamily = data.value.paragraphFont;
    }
    if (data?.value?.fontWeight) {
      data.value.fontWeight = this.convertDataFontWeight(data?.value?.fontWeight);
    }
    // fontWeight for screen
    const screens = data?.value?.screens;
    if (screens) {
      Object.keys(screens).forEach((key) => {
        const screenFontWeight = screens[key]?.fontWeight;
        if (screenFontWeight) {
          screens[key].fontWeight = this.convertDataFontWeight(screenFontWeight);
        }
      });
    }
    return data;
  }

  /**
   * updateDataTemplateSnippet
   * DOM đã được update bởi function trước đó nên tại đây chỉ update data trong Store
   */
  async updateDataTemplateSnippet(newClass: any, classRemove = '', gsId: any) {
    const $gfFrame = jQuery('#gfFrame');
    const $gryffBody = $gfFrame.contents().find('body').first();
    const $mdSettings = jQuery('#md-settings');
    const merId = `#${$mdSettings.data('edittingID')}`;
    const merType = $mdSettings.attr('data-editting');
    const $element = $gryffBody.find(merId);
    const dataKey = HELPER.getDataKeyMER($element);
    await this.handleUpdateClassGs({
      type: dataKey,
      gsId: gsId,
      merId: merId,
      merType: merType,
      newClass: newClass,
      classRemove: classRemove,
      isUpdateDataGlobalStyle: true,
    });
  }

  /**
   *updateDataTemplateSnippetForTabDs
   */
  async updateDataTemplateSnippetForTabDs(isMERRoot = false, removeClass = '', addClass = '') {
    // update template
    // if (!$targetElement) {
    //   return;
    // }
    const $gfFrame = jQuery('#gfFrame');
    const $gryffBody = $gfFrame.contents().find('body').first();
    const $gfLeftSideBar = jQuery('#gfLeftSideBar');
    const $mdSettings = jQuery('#md-settings');

    const targetId = $gfLeftSideBar.find('.md-title .mer-title').attr('data-id');
    const merId = '#' + targetId;
    const $targetWrap = $gryffBody.find(merId);

    let targetSelector = this.getTargetSelector(merId);

    if (isMERRoot) {
      targetSelector = `${merId}`;
    }

    const merType = $mdSettings.attr('data-editting');

    const attrId = merId.replace('#e-', '').replace('#m-', '').replace('#r-', '');
    const dataId = merId.replace('#', '');
    const keySettingTemp: any = HELPER.getKeyDataSetting(base._template, attrId, merType);
    const defaultTemplate = this.getDefaultTemplateAfterSetDOM(keySettingTemp);
    // -------------------
    const $merTemplate = jQuery(`<div id="${dataId}"></div>`).html(defaultTemplate);
    const $elNewTemplate = jQuery('<div />').append($merTemplate);
    $elNewTemplate.find(`${targetSelector}`).removeClass(removeClass);
    if (addClass) {
      $elNewTemplate.find(`${targetSelector}`).addClass(addClass);
    }
    if ($elNewTemplate) {
      const newTemplate = this.handleNewTemplateAfterSetToStore($elNewTemplate.find(merId).html());
      (app as any).$store.getters['editor/UPDATE_DATA_SETTING_BY_KEY'](keySettingTemp, newTemplate);
    }

    HELPER.flagListItemChange($targetWrap, 'replace');
    // if (HELPER.isInsideTheList($targetWrap)) {
    const $itemContent = $targetWrap.closest('.item-content');
    await HELPER.newDetectContentChange($itemContent);
    // }
  }

  /**
   * updateClassForElement
   */
  async updateClassForElement($mer: any, $merTemplate: any, param: any) {
    const fields = param.fields;
    const fieldValues: any = Object.values(fields);
    const length = fieldValues.length;
    const attrId = this.getAttrIdMER(param.merId) || '';
    const dataSettingGlobalStyle = new DataSettingGlobalStyle(attrId);

    for (let index = 0; index < length; index++) {
      const fieldValue = fieldValues[index];
      param.selector = (fieldValue && fieldValue.selector) || '';
      if (param?.isUpdateClassInDom) {
        this.changeClassForMER($mer, $merTemplate, param, dataSettingGlobalStyle);
      }
      if (param?.isUpdateDataGlobalStyle) {
        const newClass = param.newClass;
        if (newClass) {
          dataSettingGlobalStyle.updateValueGS({ gsId: param.gsId, selector: param.selector, class: newClass });
        }
      }
    }
    return false;
  }

  /**
   * changeClassForMER
   */
  changeClassForMER($mer: any, $merTemplate: any, param: any, dataSettingGlobalStyle: any) {
    let newClass = param.newClass;
    const selector = param.selector;
    const targetSelector: any = renderTabSettings.getTargetSelectorBySelector($mer, selector);
    const $targetSelector = targetSelector ? $mer.find(targetSelector) : $mer;
    const $targetSelectorTemplate = targetSelector ? $merTemplate.find(targetSelector) : $merTemplate;
    if (param.isApplyNewPreset) {
      if (param.classRemove) {
        $targetSelector.removeClass(param.classRemove).addClass(newClass);
        $targetSelectorTemplate.removeClass(param.classRemove).addClass(newClass);
      }
      return;
    }
    if (param.merSub && !newClass) {
      newClass = this.getClassGsFolowTitleTag($targetSelector);
    }
    if (param.isInitMER) {
      const currentClass = dataSettingGlobalStyle.getCurrentClassGsByKey(param.gsId);
      if (!currentClass && currentClass.length == 0) {
        // Case: Init New Mer
        // Handle: update value when init new element or load template old
        $targetSelector.addClass(param.defaultClassGS);
        $targetSelectorTemplate.addClass(param.defaultClassGS);
        dataSettingGlobalStyle.updateValueGS({ gsId: param.gsId, selector: selector, class: param.defaultClassGS });
      }
      if (currentClass) {
        // Case: Init New But Data MER existed
        // HOTFIX: case template or data snippet chưa được update class
        if (!$targetSelector.hasClass(currentClass)) {
          if (param.typeSetting && this.isButtonTypeSetting(param.typeSetting)) {
            // HOTFIX: case button dynamic not remove default class
            const classesRemove = this.getClassButtonGsByEl($targetSelector);
            $targetSelector.removeClass(classesRemove.join(' '));
            $targetSelectorTemplate.removeClass(classesRemove.join(' '));
          }
          $targetSelector.addClass(currentClass);
          $targetSelectorTemplate.addClass(currentClass);
        }
      }
      return;
    }
    // Case: Change Setting
    if (param.classRemove && newClass) {
      $targetSelector.removeClass(param.classRemove).addClass(newClass);
      $targetSelectorTemplate.removeClass(param.classRemove).addClass(newClass);
    }
    return;
  }

  /**
   *
   */
  convertDataSpacingByClass(spacings: any) {
    const dataSpacingByClass: any = {};
    const fields = this.getFieldsByGsId('gs-spacing');
    const fieldLength = fields.length;
    const spacingLength = spacings.length;
    for (let i = 0; i < fieldLength; i++) {
      const key = fields[i];
      for (let j = 0; j < spacingLength; j++) {
        const spacing: any = spacings[j];
        const buildClass = this.buildClassSpacing(spacing, key);
        dataSpacingByClass[buildClass] = {
          ...spacing,
          inherit: key,
        };
      }
    }
    return dataSpacingByClass || {};
  }

  /**
   *
   */
  handleUpdateClassGs(param: any) {
    const type = param.type;
    const settingGS = this.getSettingGlobalStyles(type);
    const settingGSLength = settingGS.length;
    if (settingGSLength <= 0) return false;
    const merId = param.merId || '';
    const $mer = param.mer;
    const elementConfig = this.getElMapByType(type);
    const gsClassDefault = elementConfig?.defaultClass;
    const attrId = this.getAttrIdMER(merId) || '';
    const dataId = merId.replace('#', '');
    const keySettingTemp = HELPER.getKeyDataSetting(base._template, attrId, param.merType);
    const defaultTemplate = this.getDefaultTemplateAfterSetDOM(keySettingTemp);
    // -------------------
    const $merTemplate = jQuery(`<div id="${dataId}"></div>`).html(defaultTemplate);

    for (let index = 0; index < settingGSLength; index++) {
      const itemSettingGS = settingGS[index];
      const gsId = itemSettingGS?.gsId;
      if (param.gsId && param.gsId !== gsId) {
        continue;
      }
      const titleTag = itemSettingGS['titleTag'] ? itemSettingGS['titleTag'] : '';
      const typeSetting = itemSettingGS?.typeSetting || '';
      const defaultClassGS = itemSettingGS?.defaultClass ? itemSettingGS?.defaultClass : gsClassDefault;
      const fields = (elementConfig && elementConfig.settingGroups && elementConfig.settingGroups[gsId]) || '';
      const updateClass = {
        ...param,
        gsId: gsId,
        fields: fields,
        merSub: titleTag,
        defaultClassGS: defaultClassGS,
        typeSetting: typeSetting,
      };
      this.updateClassForElement($mer, $merTemplate, updateClass);
      const newTemplate = this.handleNewTemplateAfterSetToStore($merTemplate.html());
      (app as any).$store.getters['editor/UPDATE_DATA_SETTING_BY_KEY'](keySettingTemp, newTemplate);
    }
    return false;
  }

  /**
   * initGlobalStyleClass
   */
  getClassGsFolowTitleTag($element: any) {
    let globalStyleClass = '';
    if ($element.length > 0) {
      const tagName = $element.prop('tagName').toLowerCase();
      const classHeadingMap: any = {
        h1: 'heading-1',
        h2: 'heading-2',
        h3: 'heading-3',
        h4: 'heading-4',
        h5: 'heading-5',
        h6: 'heading-6',
        p: 'paragraph-1',
      };
      globalStyleClass = `gf_gs-text-${classHeadingMap[tagName]}`;
    }
    return globalStyleClass;
  }

  /**
   * isButtonTypeSetting
   */
  isButtonTypeSetting(typeSetting: string) {
    return typeSetting == 'button';
  }

  /**
   * getSettingGlobalStyles
   * Get Array
   */
  getSettingGlobalStyles(type: any): any {
    if (!Object.keys(SETTING_GLOBAL_STYLE).includes(type)) {
      return false;
    }
    return SETTING_GLOBAL_STYLE[type];
  }

  /**
   * getSettingGlobalStyle
   * Find Object
   */
  getSettingGlobalStyle(type: string, gsId = ''): any {
    const settingGS = this.getSettingGlobalStyles(type);
    if (settingGS && settingGS.length > 0) {
      const itemSettingGS = settingGS.find((item: any) => {
        return item.gsId === gsId;
      });
      return itemSettingGS;
    }
    return {};
  }

  /**
   * getOriginalClassGS
   */
  getOriginalClassGS(classInMER = '') {
    const originalClass = classInMER && classInMER.replace(/(gf_gs\S+)/gi, '');
    let originalClassStr: any = originalClass && originalClass.split(' ');
    originalClassStr =
      (originalClassStr && originalClassStr.length && originalClassStr.filter((item: any) => item)) || [];
    originalClassStr = originalClassStr.length && originalClassStr.join('.');
    return originalClassStr;
  }

  /**
   *
   */
  setupGlobalStyle() {
    (app as any).$store.commit('sidebar/CHANGE_ACTIVE_TAB', {
      key: 'global_style',
    });
  }

  /**
   *templateSetupGlobalStyle
   */
  templateSetupGlobalStyle() {
    return `<div>
        <div class="gf_gs-setup">
        Setup Global Style
        </div>
    </div>`;
  }

  /**
   * templatePopupOnbrading
   */
  templatePopupOnboarding() {
    const { t } = i18n.global;

    const obState = (app as any).$store.state['app'].shopMeta?.onboardingGlobalStyle;
    if (obState.tabSetting) return '';
    return `<div id="onboarding-gs" class="onboarding">
        <div class="arrow" style="position: absolute; top: 45px; left: -8px;"><svg width="8" height="16" viewBox="0 0 8 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path data-v-74b3acde="" d="M8 0L8 8L8 16L0.707106 8.70711C0.316581 8.31658 0.316582 7.68342 0.707106 7.29289L8 0Z" fill="#323232"></path></svg></div>
        <div class="header">
            <div>${t('sidebarElement.selectStyleGSTooltip.title')}</div>
            <svg class="close" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 3C0 1.34315 1.34315 0 3 0H21C22.6569 0 24 1.34315 24 3V21C24 22.6569 22.6569 24 21 24H3C1.34315 24 0 22.6569 0 21V3Z" fill="#323232"/>
            <path d="M6.35355 5.64645C6.15829 5.45118 5.84171 5.45118 5.64645 5.64645C5.45118 5.84171 5.45118 6.15829 5.64645 6.35355L11.2929 12L5.64645 17.6464C5.45118 17.8417 5.45118 18.1583 5.64645 18.3536C5.84171 18.5488 6.15829 18.5488 6.35355 18.3536L12 12.7071L17.6464 18.3536C17.8417 18.5488 18.1583 18.5488 18.3536 18.3536C18.5488 18.1583 18.5488 17.8417 18.3536 17.6464L12.7071 12L18.3536 6.35355C18.5488 6.15829 18.5488 5.84171 18.3536 5.64645C18.1583 5.45118 17.8417 5.45118 17.6464 5.64645L12 11.2929L6.35355 5.64645Z" fill="#E2E2E2"/>
            </svg>
        </div>
        <div class="content">
          ${t('sidebarElement.selectStyleGSTooltip.desc')}
        </div>
    </div>`;
  }

  /**
   *templateSetupGlobalStyle
   */
  renderSetupGlobalStyle(gsId = '', isButtonSetting = false) {
    store.unset('renderColumnEnd');
    store.onChange('renderColumnEnd', () => {
      if (this.getPreset()) {
        return;
      }
      const $gfLeftSideBar = jQuery('#gfLeftSideBar');
      let gfDropDownId = `#gfDropDown_${gsId}`;
      if (isButtonSetting) {
        gfDropDownId = `#gfDropDown_${gsId}-type`;
      }

      const $gfDropDownBody = $gfLeftSideBar.find(`${gfDropDownId}`).find('.gfDropDownBody');
      const $gfGsSelect = $gfDropDownBody.find('.gf_gs-select');
      const hasGsSetup = $gfGsSelect.find('.gf_gs-setup');

      if (!hasGsSetup || !hasGsSetup.length) {
        $gfGsSelect.append(this.templateSetupGlobalStyle());
      }

      // Render Link Setup Global Style For Element Multiple Select
      const $elUsingGS = $gfLeftSideBar.find('.gf_gs-select-render');
      const lengthElUsingGS = $elUsingGS.length;
      for (let i = 0; i < lengthElUsingGS; i++) {
        const $this = $($elUsingGS[i]);
        const elId = $this.attr('id');
        const $gfDropDownBodyItem = $gfLeftSideBar.find(`#gfDropDown_${elId}`).find('.gfDropDownBody');
        const $gfGsSelectItem = $gfDropDownBodyItem.find('.gf_gs-select');
        const hasGsSetupItem = $gfGsSelectItem.find('.gf_gs-setup');
        if (!$gfDropDownBodyItem || !$gfDropDownBodyItem.length || (hasGsSetupItem && hasGsSetupItem.length)) {
          continue;
        }
        $gfGsSelectItem.append(this.templateSetupGlobalStyle());
      }

      setTimeout(() => {
        this.handlePopupOB($gfLeftSideBar, $gfDropDownBody);
      }, 200);
      this.applyEventSetupGlobalStyle($gfLeftSideBar);
    });
  }

  /**
   * SHOW HIDE POPUP ONBOARDING
   * @see https://app.clickup.com/3807076/v/dc/3m5v4-18967/3m5v4-20447
   */
  handlePopupOB($gfLeftSideBar: any, $gfDropDownBody: any) {
    const shopMeta = (app as any).$store.state['app'].shopMeta;
    const blockOBTabSetting = (window as any).blockOBTabSetting;
    if (shopMeta.onboardingGlobalStyle.tabSetting || blockOBTabSetting) {
      $gfDropDownBody.find('.onboarding').remove();
      return false;
    }

    const payload = cloneDeep((app as any).$store.state['app'].shopMeta);
    (window as any).blockOBTabSetting = true;
    payload.onboardingGlobalStyle.tabSetting = true;
    (app as any).$store.dispatch('app/updateShopMeta', payload);
    const snippetAvailable = [
      'heading',
      'text-block',
      'text-field',
      'text-area',
      'button',
      'p-cart-button',
      'dynamic-button',
      'submit-button',
      'p-view-more',
    ];

    if (!snippetAvailable.includes(this.snippetType)) return;
    if (!$gfDropDownBody) return;

    if (this.templatePopupOnboarding().length > 0) {
      const $onboarding = $gfDropDownBody.find('.onboarding');
      if ($onboarding && $onboarding.length > 0) return;
      $gfDropDownBody.append(this.templatePopupOnboarding());

      let offSetItem = $gfDropDownBody.offset();
      if (!offSetItem) return;

      $gfDropDownBody.find('.onboarding').css({
        top: offSetItem.top - 95,
      });

      const $optiscrollContent = $gfLeftSideBar.find('.optiscroll-content');
      if (['text-field', 'text-area'].includes(this.snippetType)) {
        $optiscrollContent.animate(
          {
            scrollTop: offSetItem.top - 270,
          },
          2000,
        );
      }

      $optiscrollContent.scroll(function (event: any) {
        offSetItem = $gfDropDownBody.offset();
        const top = offSetItem.top;
        let display = 'block';
        if (top < 140) {
          display = 'none';
        }

        $gfDropDownBody.find('.onboarding').css({
          top: top - 130,
          display: display,
        });
      });

      $gfLeftSideBar.find('.optiscroll-vtrack').css({
        background: 'transparent',
      });

      $gfDropDownBody
        .find('.onboarding')
        .find('.close')
        .off('click')
        .on('click', function (event: any) {
          $gfDropDownBody.find('.onboarding').remove();
          (app as any).$store.state['app']['shopMeta']['onboardingGlobalStyle']['tabSetting'] = true;
        });
    }

    return false;
  }

  /**
   * applyEventSetupGlobalStyle
   */
  applyEventSetupGlobalStyle($gfLeftSideBar: any) {
    const _this = this;
    const $gsSetup = $gfLeftSideBar.find('.gf_gs-setup');

    $gsSetup.off('click').on('click', function (event: any) {
      _this.setupGlobalStyle();
    });
  }

  /**
   * applyEventEditStyle
   */
  applyEventEditStyle($gfLeftSideBar: any) {
    this.eventEditStyle($gfLeftSideBar);
    store.onChange('renderColumnEnd', () => {
      this.eventEditStyle($gfLeftSideBar);
    });
  }

  /**
   *eventEditStyle
   */
  eventEditStyle($gfLeftSideBar: any) {
    const _this = this;
    $gfLeftSideBar
      .find('.gfDropDownBody .gf_gs-select .append')
      .off('click')
      .on('click', function (this: any, e: any) {
        const type = $(this).attr('data-type') || '';
        const style = $(this).find('i').attr('data-style') || '';
        let id;
        if (style && style == 'button-style') {
          const $option = $gfLeftSideBar.find('#gf_gs-group-setting-gs-button-type .active .append .icon');
          id = $option.attr('data-id');
        } else {
          id = $(this).find('.icon').attr('data-id') || '';
        }
        e.stopPropagation();
        _this.redirectEditGloalStyle(type, id);
      });
  }

  /**
   * getElMapByType
   */
  getElMapByType(type = '') {
    const MER_MAP: any = {
      ...ELEMENTS_MAP,
      ...MODULES_MAP,
      ...ROWS_MAP,
    };
    return MER_MAP[type];
  }

  /**
   * getElementInStore
   */
  getTargetClassElm(type = '', gsId = '') {
    const elMapByType = this.getElMapByType(type);
    const fields = (elMapByType && elMapByType.settingGroups && elMapByType.settingGroups[gsId]) || '';
    const fieldValues: any = Object.values(fields);
    const selector: any = (fields && fieldValues[0].selector) || '';
    return selector;
  }

  /**
   *redirectEditGloalStyle
   */
  redirectEditGloalStyle(type: string, id?: any) {
    const preset = this.getPreset(false);
    (app as any).$store.commit('sidebar/setCurrentPreset', preset);
    (app as any).$store.commit('sidebar/CHANGE_ACTIVE_TAB', { key: 'global_style' });
    setTimeout(() => {
      (app as any).$store.commit('sidebar/setPreviewPreset', true);
      (app as any).$store.commit('sidebar/SET_VIEW_TARGET_PRESET', 'LIVE_THEME_PRESET');
      setTimeout(() => {
        (app as any).$store.commit('sidebar/SET_COMPONENT_TARGET_GLOBAL_STYLE', type);
        (app as any).$store.commit('sidebar/SET_STYLE_ID_TARGET_GLOBAL_STYLE', id);
      });
    });
  }

  /**
   * getTargetClass
   */
  getTargetClass() {
    const $mdSettings = jQuery('#md-settings');
    const dataId = $mdSettings?.data()?.edittingID;
    let targetClass = '';
    const $gfFrame = jQuery('#gfFrame');
    const $gryffBody = $gfFrame.contents().find('body').first();
    const $el = $gryffBody.find(`#${dataId}`);
    if (dataId.includes('m-')) {
      targetClass = $el.data('gfmodule').getTargetClass();
    } else if (dataId.includes('e-')) {
      targetClass = $el.data('gfelement').getTargetClass();
    }
    return targetClass;
  }

  /**
   * getTargetSelector
   */
  getTargetSelector(merId: string) {
    const targetClass = this.getTargetClass();
    return targetClass ? `${merId} ${targetClass}` : `${merId}`;
  }

  /**
   * getSignature
   */
  getSignature(item: any) {
    return item.key || item.id;
  }

  /**
   * getClassesByTypeSetting
   */
  getClassesByTypeSetting(type = '') {
    let classes: any[] = [];
    if (!type) {
      return classes;
    }
    switch (type) {
      case 'heading':
        classes = this.headingClass();
        break;
      case 'paragraph':
        classes = this.paragraphClass();
        break;
      case 'button':
        classes = this.buttonTypeClass();
        break;
      case 'heading-paragraph':
        classes = this.headingParagraphClass();
        break;
    }
    classes = classes.concat([CLASS_GS_CUSTOM]);
    return classes;
  }

  /**
   * getDataByType
   */
  getDataByType(type = '', gsId = '') {
    const itemSettingGS: any = this.getSettingGlobalStyle(type, gsId);
    let data = [];
    switch (itemSettingGS['typeSetting']) {
      case 'heading':
        data = this.getDataHeading();
        break;
      case 'paragraph':
        data = this.getDataParagraph();
        break;
      case 'button':
        data = this.getDataButtonType(itemSettingGS['buttonKey']);
        break;
      case 'heading-paragraph':
        data = this.getDataHeadingParagraph();
        break;
    }
    return data;
  }

  /**
   * getClassesByType
   * dataKey: heading, text-block,...
   */
  getClassesByType(dataKey = '', gsId = '') {
    const itemSettingGS: any = this.getSettingGlobalStyle(dataKey, gsId);
    let classes: any[] = [];
    classes = this.getClassesByTypeSetting(itemSettingGS['typeSetting']);
    return classes;
  }

  /**
   * getTypeInput
   * param: {typeSetting = "", type = "", gsId = ""}
   */
  getTypeInput(param: any): 'textStyle' | 'buttonStyle' {
    let typeSetting = param.typeSetting;
    if (!typeSetting) {
      const itemSettingGS: any = this.getSettingGlobalStyle(param.type, param.gsId);
      typeSetting = itemSettingGS['typeSetting'];
    }
    switch (typeSetting) {
      case 'heading':
      case 'paragraph':
      case 'heading-paragraph':
        return 'textStyle';
      case 'button':
        return 'buttonStyle';
    }
    return 'textStyle';
  }
  /**
   * getPresetOptionItem
   * dataKey: heading, text-block,...
   */
  getPresetOptionItem(dataKey: any, gsId: any, className: any) {
    const presetOptions = cloneDeep(this.getDataByType(dataKey, gsId));
    let presetOption: any = className && presetOptions.find((item: any) => item.class === className);
    if (presetOption?.type === 'button') {
      const $mdSettings = jQuery('#md-settings');
      const gsButtonSize = `${gsId}-style`;
      const gsButtonSizeClass: any = $mdSettings.find(`#${gsButtonSize}`).val() || '';
      const currentSize = gsButtonSizeClass && gsButtonSizeClass.replace('gf_gs-button---', '');
      presetOption = this.convertGsValueItem(presetOption, currentSize);
    } else {
      presetOption = this.convertGsValueItem(presetOption);
    }
    if (presetOption && Object.keys(presetOption).length > 0) {
      presetOption[presetOption.type] = presetOption.value;
      return presetOption;
    }
    return {};
  }

  /**
   * getClassGsByEl
   */
  getClassGsByEl($el: any) {
    if (!$el) {
      return '';
    }
    let currentClass: any = ($el && $el.attr('class')) || '';
    currentClass = (currentClass && currentClass.match(/\s?(gf_gs)[\w-]+/g)) || [];
    currentClass = (currentClass.length && currentClass.map((item: any) => item.trim())) || [];
    return currentClass;
  }
  /**
   *
   */
  getClassButtonGsByEl($el: any) {
    if (!$el) {
      return '';
    }
    let currentClass: any = ($el && $el.attr('class')) || '';
    currentClass = (currentClass && currentClass.match(/\s?(gf_gs-button)[\w-]+/g)) || [];
    currentClass = (currentClass.length && currentClass.map((item: any) => item.trim())) || [];
    return currentClass;
  }

  /**
   * getAllFieldsByType
   */
  getAllFieldsByType(type: string): string[] {
    const elMapByType: any = this.getElMapByType(type);
    const settingGroups = elMapByType && elMapByType.settingGroups;
    if (settingGroups && Object.keys(settingGroups).length > 0) {
      const fields = Object.keys(settingGroups).reduce(
        (acc: any, val: any) => [...acc, ...Object.keys(settingGroups[val])],
        [],
      );
      return fields;
    }
    return [];
  }

  /**
   * applyValueInputGs
   */
  applyValueInputGs($container: any, presetOption: any, fields: any) {
    const fontStyle = [];
    const $gfResTool = jQuery('#gfResTool');
    if (presetOption && presetOption.value && Object.keys(presetOption.value).length) {
      const length = Object.keys(fields).length;
      for (let i = 0; i < length; i++) {
        const key = Object.keys(fields)[i];
        const value: any = Object.values(fields)[i];
        const valueInherit = value?.inherit;
        if (!value || !valueInherit) {
          continue;
        }
        let $elInput = $container.find(`#${key}`);
        if (!$elInput || !$elInput.length) {
          $elInput = $container.find(`[name="${key}"]`);
        }
        const screenType =
          $gfResTool.data('gfrestool') != undefined ? $gfResTool.data('gfrestool').getCurrentStatus() : 'lg';

        let valueCurrentGs = '';

        const screentInherit = valueInherit.replace(
          this.getLastInherit(valueInherit),
          `screens.${screenType}.${this.getLastInherit(valueInherit)}`,
        );
        valueCurrentGs = this.getValueDeepObjectByPath(presetOption, screentInherit);

        if (!valueCurrentGs) {
          valueCurrentGs = this.getValueDeepObjectByPath(presetOption, valueInherit);
        }

        if (!valueCurrentGs) {
          continue;
        }
        valueCurrentGs = this.checkAndconvertValueHasUnit(valueCurrentGs, value, presetOption);
        if (key.includes('font-style')) {
          // HOTFIX: Support for case fontStyle
          // Later will be updated into each type of GS
          const fontStyleSupport = ['bold', 'italic', 'underline'];
          const keyFontStyle = 'font-style';
          if (fontStyleSupport.includes(valueCurrentGs)) {
            fontStyle.push(valueCurrentGs);
          }
          $container.find(`[name="${keyFontStyle}"]`).attr('value', fontStyle.join(',')).val(fontStyle.join(','));
        } else {
          $elInput.attr('value', valueCurrentGs).val(valueCurrentGs);
          $elInput.attr('before-value', valueCurrentGs).val(valueCurrentGs);
          if (key == 'font') {
            const $gfDropDown = $elInput.next('.gfFontSelect');
            if ($gfDropDown.hasClass('gfFontSelect')) {
              $gfDropDown.find('select').attr('value', valueCurrentGs).val(valueCurrentGs);
            }
          }
        }
      }
    }
  }

  /**
   * checkAndconvertValueHasUnit
   */
  checkAndconvertValueHasUnit(valueCurrentGs: any, valueSettingGroup: any, presetOption: any) {
    let listUnit = valueSettingGroup?.listUnit;
    listUnit = listUnit && listUnit.split(',');
    if (!listUnit || !listUnit.length) return valueCurrentGs;
    const valueAndUnit: any = (valueCurrentGs && this.detachUnitAndValue(valueCurrentGs)) || {};
    if (listUnit.includes(valueAndUnit?.unit)) return valueCurrentGs;
    const unitDefault = listUnit[0];
    const valBaseSize = presetOption.value.baseSize || presetOption.value.textStyleValue.baseSize || '16px';
    const obBaseSizeDetach = valBaseSize && this.detachUnitAndValue(valBaseSize);
    const baseSize: any = (obBaseSizeDetach && obBaseSizeDetach.value) || 16;
    switch (valueAndUnit?.unit) {
      case 'rem':
      case 'em':
        valueCurrentGs = this.convertRemEmToPx(valueAndUnit?.value, baseSize);
        break;
      case 'px':
        valueCurrentGs = this.convertPxToRemEm(valueAndUnit?.value, baseSize);
        break;
    }
    valueCurrentGs = valueCurrentGs + unitDefault;
    return valueCurrentGs;
  }

  /**
   * checkValueExistsInTwoArray
   */
  checkValueExistsInTwoArray(arr1: any, arr2: any) {
    const legArr1 = arr1.length;
    const legArr2 = arr2.length;
    const legArr3 = [...new Set([...arr1, ...arr2])].length;
    return legArr1 + legArr2 > legArr3;
  }

  /**
   * hasClassGSCustom
   * {val: string; merSelector: any}
   */
  hasClassGSCustom(params: any) {
    const $mer = params.merSelector;
    if ($mer) {
      return $mer.hasClass(CLASS_GS_CUSTOM);
    }
    const valClass = params.valClass;
    if (valClass) {
      return valClass == CLASS_GS_CUSTOM;
    }
    const arrClass = params.arrClass;
    if (arrClass && arrClass.length > 0) {
      return arrClass.includes(CLASS_GS_CUSTOM);
    }
    return false;
  }

  /**
   * lockLeftSidebar
   */
  lockLeftSidebar(isLock = true) {
    const $gfLeftSideBar = jQuery('#gfLeftSideBar');
    if (isLock) {
      $gfLeftSideBar.addClass('is-lock');
      return;
    }
    $gfLeftSideBar.removeClass('is-lock');
  }

  /**
   * getAttrIdMER
   * merId: #m-xxx | #e-xxx
   */
  getAttrIdMER(merId = '') {
    return typeof merId == 'string' && merId.replace('#e-', '').replace('#m-', '').replace('#r-', '');
  }

  /**
   * getLastInherit
   */
  getLastInherit(inherit: any) {
    if (!inherit) return;
    const arr = inherit.split('.');
    return arr.pop();
  }

  /**
   * getDefaultTemplateAfterSetDOM
   * param: ${keyType}-${type}-${attrId}
   */
  getDefaultTemplateAfterSetDOM(keySettingTemp: string) {
    let defaultTemplate = (app as any).$store.getters['editor/GET_DATA_SETTING_BY_KEY'](keySettingTemp);
    if (defaultTemplate && typeof defaultTemplate === 'string') {
      defaultTemplate = defaultTemplate && entitiesHTML(defaultTemplate);
      defaultTemplate = convertLiquidTagToTagHtml(defaultTemplate);
    }
    return defaultTemplate;
  }

  /**
   * handleNewTemplateAfterSetToStore
   * param: templateHtml
   */
  handleNewTemplateAfterSetToStore(templateHtml: string) {
    let newTemplate = templateHtml;
    if (newTemplate && typeof newTemplate === 'string') {
      newTemplate = entitiesHTML(newTemplate);
      newTemplate = replaceBrokenHTMLTemplateContent(newTemplate);
      newTemplate = revertTagHtmlToLiquidTag(newTemplate);
    }
    return newTemplate;
  }
}
